import {
  deliveryMethodOf,
  type DeliveryMethod,
  type ExternalDeploymentEvidence,
} from '@lixionary-jira/domain';
import { ExternalEvidenceForm } from './ExternalEvidenceForm';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { ProjectSettings } from '../../api/types';
import { Alert } from '../../ui/feedback';
import { Checkbox, Field, FormSection, Input, ListEditor, Select, Textarea } from '../../ui/forms';
import type { Binding } from './types';

export interface CandidateValues {
  deliveryMethod?: DeliveryMethod;
  deliveryReason?: string;
  externalDeployment?: ExternalDeploymentEvidence;
  title: string;
  environmentId: string;
  ticketKeys: string[];
  artifactDigest: string;
  artifactRef: string;
  integrationEvidence: string;
  deploymentPlan: string;
  verificationChecks: string[];
  manualChecks: string[];
  rollbackPlan: string;
}

interface Releasable {
  key: string;
  number: number;
  title: string;
  reservedBy: string | null;
  unresolvedDescendants: string[];
}

export const fromBinding = (b: Binding): CandidateValues => ({
  deliveryMethod: deliveryMethodOf(b),
  deliveryReason: b.deliveryReason ?? '',
  externalDeployment: b.externalDeployment,
  title: b.title,
  environmentId: b.environmentId,
  ticketKeys: b.tickets?.map((t) => t.key) ?? [],
  artifactDigest: b.artifactDigest ?? '',
  artifactRef: b.artifactRef ?? '',
  integrationEvidence: b.integrationEvidence ?? '',
  deploymentPlan: b.deploymentPlan ?? '',
  verificationChecks: b.verificationChecks,
  manualChecks: b.manualChecks ?? [],
  rollbackPlan: b.rollbackPlan ?? '',
});

export const toPayload = (v: CandidateValues) => ({
  ...v,
  deliveryMethod: deliveryMethodOf(v),
  externalDeployment: v.deliveryMethod === 'external_record' ? v.externalDeployment : undefined,
  artifactDigest: v.artifactDigest || null,
  artifactRef: v.artifactRef || null,
  verificationChecks: v.verificationChecks.filter((c) => c.trim()),
  manualChecks: v.manualChecks.filter((c) => c.trim() && v.verificationChecks.includes(c)),
});

/**
 * WF-10 candidate editor: ready tickets with family checks, environment, checks and plans. Jira chooses the
 * revision when the release proceeds, and whoever proceeds is the executor.
 */
export function CandidateForm({
  projectKey,
  value,
  onChange,
  currentTickets = [],
}: {
  projectKey: string;
  value: CandidateValues;
  onChange: (v: CandidateValues) => void;
  currentTickets?: string[];
}) {
  const settings = useQuery({
    queryKey: ['settings', projectKey],
    queryFn: () => api.get<ProjectSettings>(`/projects/${projectKey}/settings`),
  });
  const releasable = useQuery({
    queryKey: ['releasable', projectKey],
    queryFn: () => api.get<Releasable[]>(`/projects/${projectKey}/releases/releasable`),
  });
  const set = (patch: Partial<CandidateValues>) => onChange({ ...value, ...patch });
  const env = settings.data?.environments.find((e) => e.id === value.environmentId);
  const selected = new Set(value.ticketKeys);
  const missingFamily = (releasable.data ?? [])
    .filter((t) => selected.has(t.key))
    .flatMap((t) =>
      t.unresolvedDescendants.filter((d) => !selected.has(d)).map((d) => `${t.key} needs ${d}`),
    );
  return (
    <>
      <FormSection title="Release">
        <Field label="Delivery method">
          {(p) => (
            <Select
              {...p}
              value={deliveryMethodOf(value)}
              onChange={(e) => set({ deliveryMethod: e.target.value as DeliveryMethod })}
            >
              <option value="github_workflow">GitHub workflow</option>
              <option value="manual">Manual deployment</option>
              <option value="external_record">Record existing deployment</option>
            </Select>
          )}
        </Field>
        {deliveryMethodOf(value) !== 'github_workflow' && (
          <>
            <Alert tone="info">
              An admin must review each request. Merge, CI, readiness and delivery evidence checks still
              apply.
            </Alert>
            <Field label="Reason for manual or existing delivery">
              {(p) => (
                <Textarea
                  {...p}
                  required
                  value={value.deliveryReason ?? ''}
                  onChange={(e) => set({ deliveryReason: e.target.value })}
                />
              )}
            </Field>
          </>
        )}
        <Field label="Title">
          {(p) => (
            <Input {...p} value={value.title} onChange={(e) => set({ title: e.target.value })} required />
          )}
        </Field>
        <Field
          label="Target environment"
          hint={
            env
              ? `${
                  env.isCompletionTarget
                    ? 'Completion target: a successful attempt closes the included tickets.'
                    : 'Not the completion target: tickets stay Pending release after success.'
                } ${
                  deliveryMethodOf(value) === 'github_workflow' && env.deployWorkflow
                    ? `Jira deploys by running ${env.deployWorkflow}${env.requiresApproval ? ' after an admin approves the revision' : ''}.`
                    : ''
                }`
              : undefined
          }
        >
          {(p) => (
            <Select
              {...p}
              value={value.environmentId}
              onChange={(e) => set({ environmentId: e.target.value })}
              required
            >
              <option value="">Choose an environment…</option>
              {(settings.data?.environments ?? []).map((e) => (
                <option
                  key={e.id}
                  value={e.id}
                  disabled={deliveryMethodOf(value) === 'github_workflow' && !e.deployWorkflow}
                >
                  {e.name}
                  {e.isCompletionTarget ? ' (completion target)' : ''}
                  {deliveryMethodOf(value) === 'github_workflow' && !e.deployWorkflow
                    ? ' — no deploy workflow'
                    : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {deliveryMethodOf(value) === 'github_workflow' &&
          settings.data &&
          !settings.data.environments.some((e) => e.deployWorkflow) && (
            <Alert tone="warning" title="No environment can be deployed to yet">
              Set a deploy workflow on an environment in the project's environment settings first.
            </Alert>
          )}
      </FormSection>
      <FormSection
        title="Tickets"
        description="Only tickets in Pending release with a current readiness snapshot. Unresolved children must be included."
      >
        {(releasable.data ?? []).length === 0 && (
          <span className="small muted">No tickets are in Pending release.</span>
        )}
        <div className="stack sm">
          {(releasable.data ?? []).map((t) => {
            const reservedElsewhere = !!t.reservedBy && !currentTickets.includes(t.key);
            return (
              <Checkbox
                key={t.key}
                label={
                  <>
                    <span className="mono">{t.key}</span> {t.title}
                  </>
                }
                description={
                  reservedElsewhere
                    ? `Reserved by ${t.reservedBy}`
                    : t.unresolvedDescendants.length
                      ? `Includes children ${t.unresolvedDescendants.join(', ')}`
                      : undefined
                }
                disabled={reservedElsewhere}
                checked={selected.has(t.key)}
                onChange={(on) =>
                  set({
                    ticketKeys: on
                      ? [...value.ticketKeys, t.key]
                      : value.ticketKeys.filter((k) => k !== t.key),
                  })
                }
              />
            );
          })}
        </div>
        {missingFamily.length > 0 && (
          <Alert tone="warning" title="Family incomplete">
            {missingFamily.join('; ')}
          </Alert>
        )}
      </FormSection>
      <FormSection
        title="Revision"
        description="Jira deploys the head of the default branch once every included ticket's pull request is merged, and checks that it contains each merge."
      >
        <div className="grid-2">
          <Field label="Artifact digest" optional>
            {(p) => (
              <Input
                {...p}
                className="mono"
                value={value.artifactDigest}
                onChange={(e) => set({ artifactDigest: e.target.value })}
                placeholder="sha256:…"
              />
            )}
          </Field>
          <Field label="Artifact reference" optional>
            {(p) => (
              <Input
                {...p}
                className="mono"
                value={value.artifactRef}
                onChange={(e) => set({ artifactRef: e.target.value })}
                placeholder="registry/app@sha256:…"
              />
            )}
          </Field>
        </div>
        <Field
          label="Integration notes"
          optional
          hint="How the changes fit together, CI links. Jira adds the merged pull requests when it freezes the revision."
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={value.integrationEvidence}
              onChange={(e) => set({ integrationEvidence: e.target.value })}
            />
          )}
        </Field>
      </FormSection>
      <FormSection title="Plans">
        <Field
          label="Deployment plan"
          optional={deliveryMethodOf(value) === 'github_workflow'}
          hint={
            deliveryMethodOf(value) === 'github_workflow'
              ? "Defaults to running the environment's deploy workflow."
              : 'Describe the execution or verification procedure for the admin review.'
          }
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              required={deliveryMethodOf(value) !== 'github_workflow'}
              value={value.deploymentPlan}
              onChange={(e) => set({ deploymentPlan: e.target.value })}
            />
          )}
        </Field>
        <div className="field">
          <span className="label">Required verification checks</span>
          <ListEditor
            label="Check"
            items={value.verificationChecks}
            onChange={(verificationChecks) =>
              set({
                verificationChecks,
                manualChecks: value.manualChecks.filter((c) => verificationChecks.includes(c)),
              })
            }
            addLabel="Add check"
            placeholder="e.g. smoke tests pass"
          />
        </div>
        {deliveryMethodOf(value) === 'github_workflow' && value.verificationChecks.some((c) => c.trim()) && (
          <div className="field">
            <span className="label">Checked by a person after the deploy</span>
            <span className="small muted">
              A successful Deploy run passes the other checks. These wait for an admin to record a result on
              the attempt.
            </span>
            {value.verificationChecks
              .filter((c) => c.trim())
              .map((c) => (
                <Checkbox
                  key={c}
                  label={c}
                  checked={value.manualChecks.includes(c)}
                  onChange={(on) =>
                    set({
                      manualChecks: on
                        ? [...value.manualChecks, c]
                        : value.manualChecks.filter((m) => m !== c),
                    })
                  }
                />
              ))}
          </div>
        )}
        <Field label="Rollback plan">
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={value.rollbackPlan}
              onChange={(e) => set({ rollbackPlan: e.target.value })}
              required
            />
          )}
        </Field>
      </FormSection>
      {value.deliveryMethod === 'external_record' && (
        <ExternalEvidenceForm
          value={value.externalDeployment}
          checks={value.verificationChecks}
          onChange={(externalDeployment) => set({ externalDeployment })}
        />
      )}
    </>
  );
}
